<script>
  import { useCssModule } from 'vue';

  /**
   * @deprecated use components/RadioNew/Radio instead.
   * if you need multiple Radio components considering using RadioGroup instead.
   */
  export default {
    name: 'll-radio',

    props: {
      /**
       * The checked/selected state of the radio button
       */
      checked: {
        type: [String, Number],
        default: undefined,
      },

      /**
       * @deprecated Use :checked or v-model:checked instead of :model-value and v-model.
       */
      modelValue: {
        type: [String, Number, null],
        default: null,
        validator(value) {
          if (value !== null) {
            throw new Error('ll-radio: use :checked or v-model:checked instead of :model-value and v-model.');
          }

          return true;
        },
      },

      /**
       * Adds error styling
       */
      hasError: Boolean,

      /**
       * A unique string to distinguish one Radio instance from another
       * and to link the label to the input; required for accessibility
       */
      id: {
        type: String,
        required: true,
      },

      /**
       * The description which appears to the right of the radio button
       */
      label: {
        type: String,
        default: '',
      },
    },

    emits: ['update:checked'],

    setup() {
      return {
        classes: useCssModule(),
      };
    },

    computed: {
      internalValue: {
        get() {
          return this.checked;
        },
        set(value) {
          this.$emit('update:checked', value);
        },
      },
    },

    created() {
      if (this.$attrs.onChange) {
        throw new Error('ll-radio: use the @update:checked event instead of @input');
      }
    },
  };
</script>

<template>
  <div class="stash-radio" data-test="ll-radio" :class="[classes.root, { error: hasError }]">
    <input :id="id" v-model="internalValue" class="sr-only" :class="classes.input" type="radio" v-bind="$attrs" />

    <label :for="id" :class="classes.label">
      {{ label }}
    </label>
  </div>
</template>

<style module>
  @reference "../../../styles/main.css";

  @layer utilities {
    .root {
      position: relative;
      margin: --spacing(1.5) 0;
    }

    .label {
      color: var(--color-ice-700);
      cursor: pointer;
      display: inline-block;
      font-size: var(--text-sm);
      font-weight: var(--font-weight-normal);
      line-height: --spacing(6);
      min-height: --spacing(9);
      overflow: visible;
      padding: --spacing(1.5) 0 --spacing(1.5) calc(20px + --spacing(3));
      position: relative;
      vertical-align: top;
    }

    .label::before {
      background: var(--color-white);
      border: 1px solid var(--color-ice-500);
      content: '';
      display: inline-block;
      vertical-align: top;
    }

    .label::before,
    .label::after {
      height: 20px;
      left: 0;
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      width: 20px;
      border-radius: 50%;
    }

    .error .label {
      color: var(--color-red-500);
    }

    .error .label::before,
    .error .input {
      border-color: var(--color-red-500);
    }

    .input:checked + .label::after {
      background: var(--color-blue-500);
      content: '';
      height: 14px;
      left: 3px;
      width: 14px;
    }

    .input:checked:disabled + .label::after {
      background: var(--color-ice-500);
    }

    .input:not(:disabled, :checked) + .label:hover::before {
      border-color: var(--color-blue-500);
    }

    @media (width >= theme(--breakpoint-lg)) {
      .root input {
        margin: 0;
      }
    }
  }
</style>
